import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { doc, serverTimestamp, setDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { COLLECTIONS } from '@/lib/collections'
import { useAuth } from '@/auth/AuthProvider'
import { DiaryService } from '@/lib/firestore/diary'
import { updateStreak } from '@/lib/streak'
import { computeSummary } from '@/diary/summary'
import { todayStr, addDays } from '@/lib/dates'
import type { DailySummary, DiaryEntry, DiaryEntryInput, MealTime } from '@/types'

interface DiaryContextValue {
  selectedDate: string
  entries: DiaryEntry[]
  summary: DailySummary | null
  loading: boolean
  setSelectedDate: (d: string) => void
  goToToday: () => void
  goToPreviousDay: () => void
  goToNextDay: () => void
  canGoToNextDay: boolean
  addEntry: (input: DiaryEntryInput) => Promise<string | null>
  updateEntry: (id: string, partial: Partial<DiaryEntry>) => Promise<void>
  deleteEntry: (id: string) => Promise<void>
  copyDay: (source: string, target: string) => Promise<number>
  copyEntry: (entry: DiaryEntry, targetDate: string, targetMealType?: MealTime) => Promise<string>
  getEntriesByMealType: (m: MealTime) => DiaryEntry[]
}

const DiaryContext = createContext<DiaryContextValue | null>(null)

export function DiaryProvider({ children }: { children: ReactNode }) {
  const { user, userProfile } = useAuth()
  const [selectedDate, setSelectedDate] = useState<string>(todayStr())
  const [entries, setEntries] = useState<DiaryEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [subFailed, setSubFailed] = useState(false)
  const lastWrittenSummaryRef = useRef<Record<string, string>>({})

  // Real-time entries for the selected day.
  useEffect(() => {
    if (!user) {
      setEntries([])
      return
    }
    // Clear immediately so a date switch never shows the prior day's data.
    setEntries([])
    setLoading(true)
    setSubFailed(false)
    const unsub = DiaryService.subscribeToEntriesForDate(
      user.uid,
      selectedDate,
      (next) => {
        setEntries(next)
        setSubFailed(false)
        setLoading(false)
      },
      () => {
        // On subscription error, entries is still the reset [] — mark the day
        // failed so the summary writer never clobbers the doc with zeros.
        setSubFailed(true)
        setLoading(false)
      },
    )
    return unsub
  }, [user, selectedDate])

  const summary = useMemo(
    () => (userProfile ? computeSummary(selectedDate, entries, userProfile) : null),
    [selectedDate, entries, userProfile],
  )

  // Debounced persist of the day's rollup to ksnap_users/{uid}/dailySummaries
  // (calendar-strip dots; exact mobile payload shape, incl. legacy fields).
  useEffect(() => {
    if (!user?.uid || !summary || loading || subFailed) return
    if (summary.date !== selectedDate) return

    const payload = {
      totalItems: entries.length,
      glycemicLoad: summary.totals.netCarbsG, // legacy mirror of the net-carb budget
      avgGIScore: -1,
      netCarbsG: summary.totals.netCarbsG,
      carbsG: summary.totals.carbsG,
      fiberG: summary.totals.fiberG,
      sugarG: summary.totals.sugarG,
      performance: summary.performance,
      micros: summary.micros,
    }

    const payloadKey = JSON.stringify(payload)
    if (lastWrittenSummaryRef.current[selectedDate] === payloadKey) return

    const saveTimeout = setTimeout(() => {
      setDoc(
        doc(db, COLLECTIONS.USERS, user.uid, 'dailySummaries', selectedDate),
        { ...payload, updatedAt: serverTimestamp() },
        { merge: true },
      )
        .then(() => {
          lastWrittenSummaryRef.current[selectedDate] = payloadKey
        })
        .catch((err) => {
          console.warn('Failed to update daily summary:', err)
        })
    }, 500)

    return () => clearTimeout(saveTimeout)
  }, [user?.uid, selectedDate, summary, entries.length, loading, subFailed])

  const addEntry = useCallback(
    async (input: DiaryEntryInput): Promise<string | null> => {
      if (!user) return null
      const id = await DiaryService.createEntry(user.uid, {
        ...input,
        date: input.date || selectedDate,
      })
      // Centralized streak update — fire-and-forget, idempotent for same-day calls.
      updateStreak(user.uid).catch(() => {})
      return id
    },
    [user, selectedDate],
  )

  const value: DiaryContextValue = {
    selectedDate,
    entries,
    summary,
    loading,
    setSelectedDate,
    goToToday: () => setSelectedDate(todayStr()),
    goToPreviousDay: () => setSelectedDate((d) => addDays(d, -1)),
    // Future days are navigable (meal planning) — mobile parity.
    goToNextDay: () => setSelectedDate((d) => addDays(d, 1)),
    canGoToNextDay: true,
    addEntry,
    updateEntry: (id, partial) => DiaryService.updateEntry(id, partial),
    deleteEntry: (id) => DiaryService.deleteEntry(id),
    copyDay: async (source, target) => {
      if (!user) return 0
      const count = await DiaryService.copyEntriesForDate(user.uid, source, target)
      if (count > 0) updateStreak(user.uid).catch(() => {})
      return count
    },
    copyEntry: async (entry, targetDate, targetMealType) => {
      const id = await DiaryService.copySingleEntry(entry, targetDate, targetMealType)
      if (user) updateStreak(user.uid).catch(() => {})
      return id
    },
    getEntriesByMealType: (m) => entries.filter((e) => e.mealType === m),
  }

  return <DiaryContext.Provider value={value}>{children}</DiaryContext.Provider>
}

export function useDiary(): DiaryContextValue {
  const ctx = useContext(DiaryContext)
  if (!ctx) throw new Error('useDiary must be used within DiaryProvider')
  return ctx
}
